import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { Field, NumberInput, fmt } from "@/components/calculators/CalcShell";
import { awgToMm2, mm2ToAwg, convert } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/conversion")({
  component: ConversionPage,
});

function Row({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function ConversionPage() {
  const [awg, setAwg] = useState<number | "">(12);
  const [mm2, setMm2] = useState<number | "">(2.5);
  const [w, setW] = useState<number | "">(1500);
  const [kw, setKw] = useState<number | "">(1.5);
  const [a, setA] = useState<number | "">(1);
  const [ma, setMa] = useState<number | "">(1000);
  const [v, setV] = useState<number | "">(400);
  const [kv, setKv] = useState<number | "">(0.4);
  const [lux, setLux] = useState<number | "">(500);
  const [area, setArea] = useState<number | "">(20);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Omregninger" subtitle="Vanlige enhetskonverteringer." />

      <div className="space-y-4">
        <Row title="AWG ↔ mm²">
          <Field label="AWG"><NumberInput value={awg} onChange={setAwg} step="1" /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{awg === "" ? "–" : `${fmt(awgToMm2(awg), 3)} mm²`}</span></div>
          <Field label="mm²"><NumberInput value={mm2} onChange={setMm2} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{mm2 === "" ? "–" : `AWG ${fmt(mm2ToAwg(mm2), 1)}`}</span></div>
        </Row>

        <Row title="W ↔ kW">
          <Field label="W"><NumberInput value={w} onChange={setW} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{w === "" ? "–" : `${fmt(convert.wToKw(w), 4)} kW`}</span></div>
          <Field label="kW"><NumberInput value={kw} onChange={setKw} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{kw === "" ? "–" : `${fmt(convert.kwToW(kw), 0)} W`}</span></div>
        </Row>

        <Row title="A ↔ mA">
          <Field label="A"><NumberInput value={a} onChange={setA} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{a === "" ? "–" : `${fmt(convert.aToMa(a), 0)} mA`}</span></div>
          <Field label="mA"><NumberInput value={ma} onChange={setMa} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{ma === "" ? "–" : `${fmt(convert.maToA(ma), 4)} A`}</span></div>
        </Row>

        <Row title="V ↔ kV">
          <Field label="V"><NumberInput value={v} onChange={setV} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{v === "" ? "–" : `${fmt(convert.vToKv(v), 4)} kV`}</span></div>
          <Field label="kV"><NumberInput value={kv} onChange={setKv} /></Field>
          <div className="text-sm">= <span className="font-mono font-semibold">{kv === "" ? "–" : `${fmt(convert.kvToV(kv), 0)} V`}</span></div>
        </Row>

        <Row title="Lux ↔ Lumen (areal)">
          <Field label="Lux"><NumberInput value={lux} onChange={setLux} /></Field>
          <Field label="Areal (m²)"><NumberInput value={area} onChange={setArea} /></Field>
          <div className="text-sm sm:col-span-2">
            = <span className="font-mono font-semibold">
              {lux === "" || area === "" ? "–" : `${fmt(convert.luxToLumen(lux, area), 0)} lm totalt`}
            </span>
          </div>
        </Row>
      </div>
    </>
  );
}
